import {
  nextRefreshDelayMs,
  REFRESH_RETRY_DELAYS_MS,
  type SearchCredentials,
  type SearchEngine,
  shouldRefreshOnWake,
} from '@shared/core/search';
import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useAuth } from 'src/auth';

import { createSearchClient, hasUsableEndpoint, type SearchClient } from '../transport';
import { fetchSearchCredentials, isPermanentCredentialsFailure } from './fetchSearchCredentials';

export type SearchCredentialsState = 'loading' | 'ready' | 'unavailable';

export interface SearchCredentialsValue {
  readonly credentials: SearchCredentials | null;
  readonly state: SearchCredentialsState;
  readonly refresh: () => Promise<SearchCredentials | null>;
  /** Multi-collection search over the current credentials; null while there are none. */
  readonly client: SearchClient | null;
}

const UNAVAILABLE_AFTER_FAILURES = 3;
const STEADY_RETRY_MS = 30_000;

/** The api setting that fills each engine's credentials endpoint. */
const ENDPOINT_SETTING: Readonly<Record<SearchEngine, string>> = {
  typesense: 'TYPESENSE_PUBLIC_URL',
  algolia: 'ALGOLIA_CLIENT_ID',
};

const SearchContext = createContext<SearchCredentialsValue>({
  credentials: null,
  state: 'unavailable',
  refresh: () => Promise.resolve(null),
  client: null,
});

export interface SearchProviderProps extends PropsWithChildren {
  readonly fetchCredentials?: (getToken: () => Promise<string | null>) => Promise<SearchCredentials | null>;
}

export function SearchProvider({ children, fetchCredentials = fetchSearchCredentials }: SearchProviderProps) {
  const { currentUser, getToken } = useAuth();
  const identity = currentUser ? `${currentUser.id}:${currentUser.organizationId ?? ''}` : null;

  const getTokenRef = useRef(getToken);
  const fetchRef = useRef(fetchCredentials);
  useEffect(() => {
    getTokenRef.current = getToken;
    fetchRef.current = fetchCredentials;
  });

  const [credentials, setCredentials] = useState<SearchCredentials | null>(null);
  const [state, setState] = useState<SearchCredentialsState>('loading');

  const loadRef = useRef<() => Promise<SearchCredentials | null>>(() => Promise.resolve(null));
  const refresh = useCallback(() => loadRef.current(), []);

  useEffect(() => {
    setCredentials(null);
    if (!identity) {
      setState('unavailable');
      loadRef.current = () => Promise.resolve(null);
      return;
    }
    setState('loading');

    let stopped = false;
    let failures = 0;
    let expiresAt: string | null = null;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let inFlight: Promise<SearchCredentials | null> | null = null;

    const schedule = (delayMs: number) => {
      clearTimeout(timer);
      if (!stopped) timer = setTimeout(() => void load(), delayMs);
    };

    // Drops the credentials; with `expiresAt` null, the next wake (tab focus, back online) refetches.
    const becomeUnavailable = (): null => {
      expiresAt = null;
      setCredentials(null);
      setState('unavailable');
      return null;
    };

    const attempt = async (): Promise<SearchCredentials | null> => {
      try {
        const next = await fetchRef.current(() => getTokenRef.current());
        if (stopped) return null;
        failures = 0;
        if (next === null) {
          // A keyless mint is either nothing to search or a minter outage, which the response does not tell
          // apart; keep retrying so an outage heals without waiting for a wake.
          becomeUnavailable();
          schedule(STEADY_RETRY_MS);
          return null;
        }
        if (!hasUsableEndpoint(next)) {
          console.warn(
            { engine: next.engine, setting: ENDPOINT_SETTING[next.engine] },
            'Search credentials carry no usable endpoint; search is unavailable'
          );
          return becomeUnavailable();
        }
        expiresAt = next.expiresAt;
        setCredentials(next);
        setState('ready');
        schedule(nextRefreshDelayMs(next.expiresAt, Date.now(), Math.random));
        return next;
      } catch (error) {
        if (stopped) return null;
        if (isPermanentCredentialsFailure(error)) return becomeUnavailable();
        failures += 1;
        if (failures >= UNAVAILABLE_AFTER_FAILURES) becomeUnavailable();
        schedule(REFRESH_RETRY_DELAYS_MS[failures - 1] ?? STEADY_RETRY_MS);
        return null;
      }
    };

    const load = (): Promise<SearchCredentials | null> => {
      inFlight ??= attempt().finally(() => {
        inFlight = null;
      });
      return inFlight;
    };

    const onWake = () => {
      if (document.visibilityState === 'hidden') return;
      if (expiresAt === null || shouldRefreshOnWake(expiresAt, Date.now())) void load();
    };

    loadRef.current = load;
    document.addEventListener('visibilitychange', onWake);
    window.addEventListener('online', onWake);
    void load();

    return () => {
      stopped = true;
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', onWake);
      window.removeEventListener('online', onWake);
    };
  }, [identity]);

  const client = useMemo(() => (credentials === null ? null : createSearchClient(credentials)), [credentials]);
  const value = useMemo(() => ({ credentials, state, refresh, client }), [credentials, state, refresh, client]);

  return <SearchContext.Provider value={value}>{children}</SearchContext.Provider>;
}

export const useSearchCredentials = (): SearchCredentialsValue => useContext(SearchContext);
